import { SlideStatement } from "../../../SlideStatement";
import { SlideVerdict } from "../../../SlideVerdict";
import { SupLink } from "../../../SlideContext";

const BOTTOM_BADGES = [
  "Too manual. Unreliable automation.",
  "Optimized for outdated store algorithms.",
  "Legacy tools push raw data to human interpretation \u2014 providing no insights or recommendations.",
];

type Corner = "tl" | "tr" | "bl" | "br";

const PRESSURES: Array<{ corner: Corner; lead: string; body: string }> = [
  {
    corner: "tl",
    lead: "KPIs decay",
    body: "Pause, and last month's gains are already slipping.",
  },
  {
    corner: "tr",
    lead: "Competitors iterate",
    body: "Rivals evolve their marketing constantly.",
  },
  {
    corner: "bl",
    lead: "Surfaces rerank",
    body: "Apple, Google & the AI assistants reshuffle results daily.",
  },
  {
    corner: "br",
    lead: "A global mandate",
    body: "More markets, more languages, more competitors.",
  },
];

export default function TheProblem() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="The Problem">
        <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
          ASO has never been more valuable.
          <br className="hidden md:block" />
          Or harder to do correctly.
        </h1>
      </SlideStatement>

      {/* Below the rule — the substantiation, as one evenly-spaced stack:
          the scale of the problem, the forces behind it, the diagnosis, and
          the conclusion last. One gap value governs every seam, so the rhythm
          holds when any section changes height. */}
      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:gap-6 md:pt-6">
        <p className="text-xl font-medium tracking-tight text-slate-700 md:text-2xl">
          Your app is one of 4.5M+ on the App Store &amp; Google Play and ~4,000
          more launch every day<SupLink to="sources">1</SupLink>.
          <br className="hidden md:block" />
          You must iterate your GTM constantly, across ever more surfaces.
        </p>

        {/* Visual band — pressure diagram + decay curve */}
        <div className="grid grid-cols-1 gap-4 md:min-h-0 md:flex-1 md:grid-cols-[1.45fr_1fr] md:gap-6">
          <PressurePanel />
          <DecayPanel />
        </div>

        {/* The diagnosis */}
        <div className="flex flex-col gap-3 rounded-xl bg-slate-50 px-5 py-3 ring-1 ring-slate-200 md:w-fit md:flex-row md:items-center md:gap-12 md:self-center md:px-10 md:py-2.5">
          <p className="text-balance text-base font-bold leading-snug tracking-tight text-slate-900 md:max-w-[380px] md:shrink-0 md:text-2xl">
            You're using antiquated tooling built for the pre-AI era of app
            marketing.
          </p>
          <ul className="flex flex-col gap-1.5 md:gap-1.5">
            {BOTTOM_BADGES.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-sm leading-snug text-slate-700 md:text-base"
              >
                <span aria-hidden="true" className="shrink-0 font-bold text-rose-500">
                  ✕
                </span>
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* The conclusion */}
        <SlideVerdict>ASO became continuous and your tools aren't ready.</SlideVerdict>
      </div>
    </div>
  );
}

/* ---------- Pressure diagram ---------- */

function PressurePanel() {
  return (
    <div className="flex min-h-0 flex-col rounded-xl bg-white p-5 pb-3 shadow-sm ring-1 ring-slate-200 md:p-4 md:pb-2">
      <h3 className="text-lg font-bold leading-tight tracking-tight text-blue-600 md:text-xl">
        Four dynamic forces.
      </h3>
      <div className="mt-1 grid min-h-0 max-h-[220px] flex-1 grid-cols-[1fr_auto_1fr] items-center gap-x-3 md:mt-1.5 md:max-h-none">
        <div className="flex h-full min-h-0 flex-col justify-around gap-1.5 py-0">
          <PressureLabel {...PRESSURES[0]} />
          <PressureLabel {...PRESSURES[2]} />
        </div>
        <AppTile />
        <div className="flex h-full min-h-0 flex-col justify-around gap-1.5 py-0">
          <PressureLabel {...PRESSURES[1]} />
          <PressureLabel {...PRESSURES[3]} />
        </div>
      </div>
    </div>
  );
}

function PressureLabel({
  corner,
  lead,
  body,
}: {
  corner: Corner;
  lead: string;
  body: string;
}) {
  const isLeft = corner === "tl" || corner === "bl";
  return (
    <div className={isLeft ? "text-right" : "text-left"}>
      <p className="text-sm font-bold leading-tight text-slate-900 md:text-[15px]">
        {lead}
      </p>
      <p className="mt-0.5 text-xs leading-snug text-slate-600 md:text-[13px]">
        {body}
      </p>
    </div>
  );
}

// Base arrow points ↘ with its tip at the glyph's bottom-right; each corner
// placement rotates it so the tip lands nearest the tile corner, aimed at it.
function PressureArrow({
  rotation,
  className,
}: {
  rotation: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 36 36"
      aria-hidden="true"
      className={`h-7 w-7 shrink-0 md:h-9 md:w-9 ${className ?? ""}`}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <line
        x1="5"
        y1="5"
        x2="21"
        y2="21"
        stroke="#dc2626"
        strokeWidth="2.5"
        strokeDasharray="5,4"
        strokeLinecap="round"
      />
      <path d="M 30 30 L 27.1 20.1 L 20.1 27.1 z" fill="#dc2626" />
    </svg>
  );
}

function AppTile() {
  return (
    <div className="flex flex-col items-center px-8 py-1 md:px-10 md:py-0">
      <div className="relative">
        {/* Arrows pinned to the tile's corners, each aimed at its corner */}
        <PressureArrow rotation={0} className="absolute -left-8 -top-8 md:-left-10 md:-top-10" />
        <PressureArrow rotation={90} className="absolute -right-8 -top-8 md:-right-10 md:-top-10" />
        <PressureArrow rotation={270} className="absolute -left-8 -bottom-8 md:-left-10 md:-bottom-10" />
        <PressureArrow rotation={180} className="absolute -right-8 -bottom-8 md:-right-10 md:-bottom-10" />
        {/* The tile carries its own label (TPF, 2026-09-08) — the app-grid
            glyph and the caption beneath it were saying the same thing twice. */}
        <div className="flex h-16 w-16 items-center justify-center rounded-[22%] bg-blue-600 px-2 text-center shadow-md md:h-20 md:w-20">
          <p className="text-sm font-bold leading-tight text-white md:text-base">
            Your
            <br />
            app
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Decay curve ---------- */

function DecayPanel() {
  return (
    <div className="flex min-h-0 flex-col rounded-xl bg-white p-5 pb-3 shadow-sm ring-1 ring-slate-200 md:p-4 md:pb-2">
      <h3 className="text-lg font-bold leading-tight tracking-tight text-blue-600 md:text-xl">
        Competitive means continuous
      </h3>

      <div className="relative mt-3 flex min-h-0 flex-1 flex-col md:mt-2">
        {/* x-origin 28, not 0: the y-axis is the leftmost drawn element, so
            starting the viewBox at it makes the axis flush with the card's
            text column instead of inset by a 28-unit gutter. Width 332 keeps
            the original 12-unit right margin (28 + 332 = 360); height 208 trims the
            dead band that used to sit below the x-axis at y=198. */}
        <svg
          viewBox="28 0 332 208"
          preserveAspectRatio="none"
          className="h-36 w-full md:h-full md:min-h-0 md:flex-1"
          role="img" aria-label="Conceptual chart: with continuous ASO an app's visibility and conversion keep rising; pause and they decay — the gap compounds.">
          {/* Axes */}
          <path
            d="M 28 18 L 28 198 L 348 198"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.5"
          />
          <path
            d="M 342 193 L 348 198 L 342 203"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.5"
          />
          {/* Shared path up to the fork */}
          <path
            d="M 28 126 C 50 123 72 120 92 118"
            fill="none"
            stroke="#475569"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <circle cx="92" cy="118" r="3.5" fill="#475569" />

          {/* Continuous ASO — compounds upward */}
          <path
            d="M 92 118 C 150 112 230 92 290 64 C 315 52 332 42 344 34"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2.5"
            strokeLinecap="round"
          />

          {/* Reactive ASO — decays */}
          <path
            d="M 92 118 C 140 128 200 152 250 170 C 290 183 320 189 344 191"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        {/* Series labels live in HTML, not in the SVG: the chart stretches to
            the card with preserveAspectRatio="none", which is fine for curves
            and ruinous for glyphs. Percentages track the curve endpoints. */}
        <p className="pointer-events-none absolute right-0 top-[6%] text-xs font-semibold text-blue-600 md:text-[13px]">
          Continuous ASO
        </p>
        <p className="pointer-events-none absolute right-0 top-[74%] text-xs font-semibold text-red-600 md:text-[13px]">
          Reactive ASO
        </p>
      </div>
    </div>
  );
}
